/**
 * M7 S40 part 3: the live restart defence and the Wrecker squat fight (plan S40; spec §16.3 steps 2 and 5).
 *
 * - **The restart defence** (`beginRestart`, the live loop's dep): M3's relay reboot runtime reused, not copied: a
 *   siege objective (`relink/objective.ts createSiegeObjective`) at the derelict's repair port with the derelict's
 *   streamed colliders remapped to it, the region's Wrecker waves and (a vein within the hop rule) the guardians from
 *   `restartPlan(...).schedule` through `session.spawnGroup`; the guardian group holds an `addGrudge` against the
 *   objective's side while it lives. It runs in `core.relink` (phase `restart`, `relayIndex` −1), so the HUD's reboot
 *   readout shows it, a relay reboot can't start while it runs (`beginReboot` and the relay prompts are busy) and it
 *   never starts while a reboot runs. It ends through the M3 `REBOOT_END` codes: hull 0 (`destroyed`, a scram), the
 *   player dead, beyond `leaveAbortM` or out of the system (`aborted`), or the timer (`online`), then
 *   `restartOutcomeOf` → `settleRestart` → `service.apply` (online: `restoreDerelict` goes out to the worker and the
 *   record is `lit`, before S43's registration; scram: the fuel cells go, the retry waits an online day; aborted:
 *   nothing changes). No relay events are emitted (the story would read them as a relay reboot).
 * - **The Wrecker squat** (`fight`): calling it out spawns `squatWaves` (the region's raider waves) all at once around
 *   the derelict on the raider team (`tuning.json squat.spawnRadiusM`); once every group is dead the squat is
 *   `markSquatFought` through `service.apply`; paying the fence while it is out despawns it (`paid`). Dying, leaving
 *   the system or flying beyond `squat.leaveAbortM` breaks the fight off (the squat despawns, nothing is marked).
 *
 * `step` is allocation-free (D-014): float64 positions are read in place from the run state; starts, spawns, remaps
 * and outcomes are rare events and allocate.
 */
import { z } from 'zod'
import type { Career } from '../../career/types'
import { GUARDIAN_TEAM_ID } from '../../charter/hostilityWriter'
import { addGrudge } from '../../combat/hostility'
import { SIM_DT } from '../../core/loop'
import { logger } from '../../core/logger'
import type { Vec3T } from '../../galaxy/layout/types'
import { minutesSeconds } from '../../relink/commands'
import { loadRelinkConfig, type RelinkConfig } from '../../relink/config'
import { createSiegeObjective, remapStructureColliders, removeSiegeObjective, restoreStructureColliders } from '../../relink/objective'
import { REBOOT_END, type RebootEnd } from '../../relink/reboot'
import type { ScheduledSpawn } from '../../relink/rules'
import type { ReachCore } from '../../reach/runtime/types'
import type { ReachCommandResult } from '../../reach/types'
import { PLAYER_TEAM, RAIDER_TEAM, type Sim } from '../../sim'
import { storyData } from '../../story/data'
import type { SurveyCard } from '../../story/reactivation/survey'
import type { ReactivationConfig } from '../../story/schemas'
import { Hull, Life, Pos64, Team } from '../../world/components'
import type { StoryService } from '../service'
import { fillTokens } from '../world'
import type { LiveSite, ReactivationSquatPort } from './live'
import { restartOutcomeOf, restartPlan, settleRestart, type RestartOutcome } from './reboot'
import { markSquatFought, refusalText, squatWaves } from './restore'
import { reactivationText, type ReactivationText } from './survey'
import tuningJson from './tuning.json'

const range = z.tuple([z.number().positive(), z.number().positive()]).refine(([a, b]) => a <= b, 'min ≤ max')
export const reactivationTuningSchema = z.strictObject({
  comment: z.string(),
  squat: z.strictObject({ spawnRadiusM: range, despawnM: z.number().positive(), leaveAbortM: z.number().positive() }),
})
export type ReactivationTuning = z.output<typeof reactivationTuningSchema>

export function loadReactivationTuning(): ReactivationTuning {
  return reactivationTuningSchema.parse(tuningJson)
}

/** The restart in progress (the HUD's half lives in `core.relink`). */
export interface RestartRun {
  /** '' when idle. */
  poiId: string
  card: SurveyCard | null
  systemId: string
  /** The repair port (system frame, float64): the objective's point and the spawn centre. */
  center: Vec3T
  facing: Vec3T
  schedule: ScheduledSpawn[]
  next: number
  handles: number[]
  structuresVersion: number
  guardianGroup: number
}

/** The Wrecker squat that is out. */
export interface SquatRun {
  /** '' when none. */
  poiId: string
  card: SurveyCard | null
  systemId: string
  center: Vec3T
  groups: number[]
}

export interface ReactivationDefenceDeps {
  service: Pick<StoryService, 'apply'>
  career(): Career | null
  say(text: string): void
  cfg?: ReactivationConfig
  relink?: RelinkConfig
  tuning?: ReactivationTuning
  text?: ReactivationText
}

export interface ReactivationDefence {
  /** The live loop's `beginRestart` (the caller checked `restartRefusal`). */
  beginRestart(site: LiveSite, core: ReachCore, sim: Sim): ReachCommandResult
  /** The live loop's squat port. */
  readonly squat: ReactivationSquatPort
  /** One sim tick (the story subsystem's step). Allocation-free. */
  step(core: ReachCore, sim: Sim): void
  /** The system is being left: the restart aborts, the squat fight breaks off. */
  leave(core: ReachCore, sim: Sim): void
  readonly restart: Readonly<RestartRun>
  readonly squatRun: Readonly<SquatRun>
}

const refuse = (error: Exclude<ReachCommandResult, { ok: true }>['error'], message: string): ReachCommandResult => ({ ok: false, error, message })

/** The unit vector from the site toward the player (+Z when they coincide). Rare. */
function facingOf(sim: Sim, x: number, y: number, z: number): Vec3T {
  const p = sim.playerEid
  const dx = p >= 0 ? Pos64.x[p] - x : 0
  const dy = p >= 0 ? Pos64.y[p] - y : 0
  const dz = p >= 0 ? Pos64.z[p] - z : 0
  const len = Math.sqrt(dx * dx + dy * dy + dz * dz)
  return len > 1e-6 ? [dx / len, dy / len, dz / len] : [0, 0, 1]
}

export function createReactivationDefence(deps: ReactivationDefenceDeps): ReactivationDefence {
  const cfg = deps.cfg ?? storyData().reactivation
  const relink = deps.relink ?? loadRelinkConfig()
  const tuning = deps.tuning ?? loadReactivationTuning()
  const text = deps.text ?? reactivationText()
  const run: RestartRun = {
    poiId: '',
    card: null,
    systemId: '',
    center: [0, 0, 0],
    facing: [0, 0, 1],
    schedule: [],
    next: 0,
    handles: [],
    structuresVersion: -1,
    guardianGroup: -1,
  }
  const squatRun: SquatRun = { poiId: '', card: null, systemId: '', center: [0, 0, 0], groups: [] }

  /** Maps the derelict's streamed colliders to the objective again (after a re-stream; rare). */
  function remap(core: ReachCore, sim: Sim, eid: number): void {
    restoreStructureColliders(sim.physics, run.handles, eid)
    run.handles = []
    remapStructureColliders(sim.physics, run.center[0], run.center[1], run.center[2], eid, run.handles)
    run.structuresVersion = core.session.structuresVersion
  }

  function beginRestart(site: LiveSite, core: ReachCore, sim: Sim): ReachCommandResult {
    if (core.relink.phase !== 'idle' || run.poiId !== '') return refuse('busy', text.refusals.busy)
    const layout = core.session.layout
    if (layout === null || layout.systemId !== site.card.systemId) return refuse('invalid', text.refusals.range)
    if (core.travel.mode !== 'normal') return refuse('busy', text.refusals.speed)
    const plan = restartPlan(core.galaxy, { id: layout.systemId, region: layout.region }, relink)
    const p = sim.playerEid
    const team = p >= 0 ? Team.id[p] : PLAYER_TEAM
    const reboot = relink.reboot
    const eid = createSiegeObjective(sim, site.x, site.y, site.z, team, reboot.hull, reboot.siegeWeight)
    run.poiId = site.card.poiId
    run.card = site.card
    run.systemId = layout.systemId
    run.center = [site.x, site.y, site.z]
    run.facing = facingOf(sim, site.x, site.y, site.z)
    run.schedule = plan.schedule
    run.next = 0
    run.handles = []
    run.guardianGroup = -1
    remap(core, sim, eid)
    const r = core.relink
    r.relayIndex = -1
    r.phase = 'restart'
    r.t = 0
    r.duration = plan.seconds
    r.hull = reboot.hull
    r.hullMax = reboot.hull
    r.wave = 0
    r.waves = plan.schedule.length
    r.objectiveEid = eid
    logger.info(`reactivation: restart of ${site.card.designation} started (${plan.seconds} s, ${plan.schedule.length} waves${plan.guardians ? ', guardians' : ''})`)
    const message = fillTokens(text.messages.restartStarted, { time: minutesSeconds(plan.seconds), waves: String(plan.schedule.length) })
    deps.say(message)
    return { ok: true, message }
  }

  /** Spawns the next scheduled wave around the derelict (rare: allocates). */
  function spawnNext(core: ReachCore): void {
    const entry = run.schedule[run.next]
    run.next++
    if (entry === undefined) return
    const guardian = entry.kind === 'guardian'
    const group = core.session.spawnGroup({
      ships: entry.ships,
      center: run.center,
      facing: run.facing,
      radiusM: relink.reboot.spawnRadiusM,
      team: guardian ? GUARDIAN_TEAM_ID : RAIDER_TEAM,
      kind: entry.kind,
      despawnM: relink.reboot.despawnM,
    })
    if (guardian) run.guardianGroup = group
    core.relink.wave++
  }

  /** Settles an outcome through the story service and says it (rare). */
  function settle(card: SurveyCard, outcome: RestartOutcome): void {
    const career = deps.career()
    if (career === null) return
    const first = settleRestart(career, card, outcome, cfg, text)
    if (outcome !== 'aborted') deps.service.apply(`reactivation restart ${outcome}`, (c) => settleRestart(c, card, outcome, cfg, text).result)
    deps.say(first.message)
  }

  /** Ends a running restart with `end` (a no-op when none runs). Rare: allocates. */
  function endRestart(core: ReachCore, sim: Sim, end: RebootEnd): void {
    const r = core.relink
    if (r.phase !== 'restart') return
    const card = run.card
    restoreStructureColliders(sim.physics, run.handles, r.objectiveEid)
    removeSiegeObjective(sim, r.objectiveEid)
    r.relayIndex = -1
    r.phase = 'idle'
    r.t = 0
    r.duration = 0
    r.hull = 0
    r.hullMax = 0
    r.wave = 0
    r.waves = 0
    r.objectiveEid = -1
    run.poiId = ''
    run.card = null
    run.systemId = ''
    run.schedule = []
    run.next = 0
    run.handles = []
    run.structuresVersion = -1
    run.guardianGroup = -1
    const outcome = restartOutcomeOf(end)
    if (card !== null) {
      logger.info(`reactivation: restart of ${card.designation} ended (${outcome})`)
      // After the reset: the online autosave needs the relink phase idle.
      settle(card, outcome)
    }
  }

  function stepRestart(core: ReachCore, sim: Sim): void {
    const r = core.relink
    if (r.phase !== 'restart') return
    const layout = core.session.layout
    if (layout === null || layout.systemId !== run.systemId) {
      endRestart(core, sim, REBOOT_END.aborted)
      return
    }
    const o = r.objectiveEid
    r.t += SIM_DT
    r.hull = Hull.value[o]
    if (run.structuresVersion !== core.session.structuresVersion) remap(core, sim, o)
    while (run.next < run.schedule.length && run.schedule[run.next].atS <= r.t) spawnNext(core)
    if (run.guardianGroup >= 0 && core.session.groupAlive(run.guardianGroup) > 0) addGrudge(GUARDIAN_TEAM_ID, Team.id[o], sim.time)
    const p = sim.playerEid
    if (r.hull <= 0) endRestart(core, sim, REBOOT_END.destroyed)
    else if (p < 0 || Life.alive[p] !== 1) endRestart(core, sim, REBOOT_END.aborted)
    else {
      const at = run.center
      const dx = Pos64.x[p] - at[0]
      const dy = Pos64.y[p] - at[1]
      const dz = Pos64.z[p] - at[2]
      const leave = relink.leaveAbortM
      if (dx * dx + dy * dy + dz * dz > leave * leave) endRestart(core, sim, REBOOT_END.aborted)
      else if (r.t >= r.duration) endRestart(core, sim, REBOOT_END.online)
    }
  }

  // ---------------------------------------------------------------------------------------------
  // The Wrecker squat
  // ---------------------------------------------------------------------------------------------

  /** Despawns the squat's groups and forgets it (rare). */
  function clearSquat(core: ReachCore | null): void {
    if (core !== null) for (const g of squatRun.groups) core.session.despawnGroup(g)
    squatRun.poiId = ''
    squatRun.card = null
    squatRun.systemId = ''
    squatRun.groups = []
  }

  function fight(site: LiveSite, core: ReachCore, sim: Sim): ReachCommandResult {
    if (squatRun.poiId !== '' || core.relink.phase !== 'idle') return refuse('busy', text.refusals.busy)
    const layout = core.session.layout
    if (layout === null || layout.systemId !== site.card.systemId) return refuse('invalid', text.refusals.range)
    const career = deps.career()
    if (career === null) return refuse('unavailable', '')
    // Read the record the same way the marker will (refused when nothing is left to fight).
    const check = markSquatFought(career, site.card, text)
    if (!check.ok) {
      const message = refusalText(check.reason, relink, text)
      deps.say(message)
      return refuse('invalid', message)
    }
    const s = tuning.squat
    const center: Vec3T = [site.x, site.y, site.z]
    const facing = facingOf(sim, site.x, site.y, site.z)
    const groups: number[] = []
    for (const w of squatWaves(layout.region, relink)) {
      groups.push(core.session.spawnGroup({ ships: w.ships, center, facing, radiusM: s.spawnRadiusM, team: RAIDER_TEAM, kind: w.kind, despawnM: s.despawnM }))
    }
    squatRun.poiId = site.card.poiId
    squatRun.card = site.card
    squatRun.systemId = layout.systemId
    squatRun.center = center
    squatRun.groups = groups
    logger.info(`reactivation: the Wrecker squat at ${site.card.designation} is out (${groups.length} groups)`)
    const message = fillTokens(text.messages.squatOut, { fee: String(site.card.fenceFee) })
    deps.say(message)
    return { ok: true, message }
  }

  function stepSquat(core: ReachCore, sim: Sim): void {
    if (squatRun.poiId === '') return
    const layout = core.session.layout
    const p = sim.playerEid
    let broken = layout === null || layout.systemId !== squatRun.systemId || p < 0 || Life.alive[p] !== 1
    if (!broken) {
      const at = squatRun.center
      const dx = Pos64.x[p] - at[0]
      const dy = Pos64.y[p] - at[1]
      const dz = Pos64.z[p] - at[2]
      const leave = tuning.squat.leaveAbortM
      broken = dx * dx + dy * dy + dz * dz > leave * leave
    }
    if (broken) {
      clearSquat(core)
      deps.say(text.messages.squatLeft)
      return
    }
    const groups = squatRun.groups
    let alive = 0
    for (let i = 0; i < groups.length; i++) alive += core.session.groupAlive(groups[i])
    if (alive === 0) squatDown()
  }

  /** Every squat ship is dead: the squat is marked fought (rare: allocates). */
  function squatDown(): void {
    const card = squatRun.card
    clearSquat(null)
    if (card === null) return
    const career = deps.career()
    const first = career === null ? null : markSquatFought(career, card, text)
    if (first?.ok !== true) return
    deps.service.apply('reactivation squat fought', (c) => {
      const r = markSquatFought(c, card, text)
      return r.ok ? { career: r.career, commands: [], effects: r.effects } : { career: c, commands: [], effects: [] }
    })
    deps.say(first.message)
  }

  const squat: ReactivationSquatPort = {
    running: (poiId) => squatRun.poiId === poiId,
    fight,
    paid(poiId, core) {
      if (squatRun.poiId === poiId) clearSquat(core)
    },
  }

  return {
    beginRestart,
    squat,
    restart: run,
    squatRun,
    step(core: ReachCore, sim: Sim): void {
      stepRestart(core, sim)
      stepSquat(core, sim)
    },
    leave(core: ReachCore, sim: Sim): void {
      endRestart(core, sim, REBOOT_END.aborted)
      if (squatRun.poiId !== '') clearSquat(core)
    },
  }
}
